"use client"

import { useState } from "react"
import Link from "next/link"
import { ArrowRight } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"

const typeColors = {
  Ethics: "bg-primary/10 text-primary",
  Policy: "bg-chart-4/10 text-chart-4",
  Situational: "bg-chart-2/10 text-chart-2",
  Roleplay: "bg-chart-3/10 text-chart-3",
  Personal: "bg-chart-1/10 text-chart-1",
  Behavioral: "bg-chart-6/10 text-chart-6",
  Reflective: "bg-chart-7/10 text-chart-7",
}

const examples = [
  {
    type: "Ethics",
    id: 111,
    question: "Is it ethical for pharmaceutical companies to charge high prices for life-saving medications?",
    framework: "Opinion Framework (Ethics)",
    frameworkDesc: "Name the dilemma, present both sides, commit to a position, acknowledge its limits.",
    steps: [
      { name: "Acknowledge the tension", text: `"This gets at the tension between two legitimate interests: the right of patients to access life-saving medication regardless of income, and the economic reality that drug development requires massive investment..."` },
      { name: "Explore perspectives", text: `"Without profit incentives, companies may not invest in R&D for rare diseases. On the other hand, when a company charges $100,000 for a drug that costs $5 to produce, the gap raises serious questions about who medications are being developed for..."` },
      { name: "Take a position", text: `"I believe some form of pricing regulation is justified for life-saving medications, because allowing market forces alone to determine access to drugs people will die without creates an inherently coercive dynamic..."` },
      { name: "Address limitations", text: `"The challenge is that heavy-handed price caps can reduce investment in future development. Any regulation would need tiered pricing or publicly funded R&D for diseases where the market alone won't deliver..."` },
    ],
  },
  {
    type: "Policy",
    id: 209,
    question: "Do you think the legalisation of assisted dying for terminally ill adults is ethically justified?",
    framework: "Opinion Framework (Policy)",
    frameworkDesc: "Lead with context, weigh both sides, take a position, address implementation.",
    steps: [
      { name: "Establish context", text: `"Assisted dying is now legal in a growing number of jurisdictions. Canada expanded eligibility beyond terminal illness. Several US states and European countries have adopted various models. The evidence base on implementation is still developing..."` },
      { name: "Weigh pros and cons", text: `"Proponents argue this is the logical extension of patient autonomy - that a competent person facing unbearable suffering should have the right to choose. Opponents argue it fundamentally changes the relationship between medicine and death, and that vulnerable people may feel pressured..."` },
      { name: "Take a position", text: `"I believe assisted dying for terminally ill adults, with robust safeguards, is ethically justified - because denying someone agency over their own death when they are facing inevitable suffering is itself a form of harm..."` },
      { name: "Address implementation", text: `"The critical question is safeguards. Any policy needs independent assessment of capacity, cooling-off periods, and protection against coercion. The expansion to non-terminal conditions raises more difficult questions that I think deserve separate consideration..."` },
    ],
  },
  {
    type: "Situational",
    id: 124,
    question: "You notice an intern consistently dismisses female patients' pain as 'anxiety' while taking male patients' identical symptoms seriously. What do you do?",
    framework: "Situational Framework",
    frameworkDesc: "Identify the issue, gather information, consider stakeholders, propose if/then solutions.",
    steps: [
      { name: "Identify the core issue", text: `"The most pressing concern is patient safety - if pain is being dismissed based on gender rather than clinical evidence, patients are receiving inadequate care. There's also a professional development issue for the intern..."` },
      { name: "Gather missing information", text: `"Before acting, I'd want to understand the pattern better. Is this consistent or have I seen a few isolated cases? Is the intern aware of this tendency? Are there other factors I might be missing - different clinical presentations, for example?"` },
      { name: "Consider stakeholders", text: `"The patients are most vulnerable here - they deserve equitable care. But I'd also consider the intern, who may be unaware of an implicit bias, and the broader care team and institution..."` },
      { name: "Propose solutions (if/then)", text: `"If this seems like unconscious bias, I'd start with a private, respectful conversation - framing it as an observation, not an accusation. If the pattern continues after that conversation, I'd escalate to the supervising attending..."` },
    ],
  },
  {
    type: "Roleplay",
    id: 335,
    question: "You are hosting a small gathering at your home. Your neighbor Bryce has knocked on your door. You have met Bryce a few times in passing. It is 10:30 PM on a Saturday night.",
    framework: "Roleplay Framework",
    frameworkDesc: "Show empathy and build rapport, gather information through conversation, guide forward.",
    steps: [
      { name: "Show empathy, build rapport", text: `"Hey Bryce, thanks for coming over. Is everything okay? [Warm tone, open body language. Don't assume why they're there - it could be a noise complaint, a request for help, or something else entirely.]"` },
      { name: "Gather information", text: `"[Listen to what Bryce says. Ask open-ended follow-ups: 'Can you tell me more about what's going on?' Adapt based on their response - if it's a noise complaint, be receptive. If they seem distressed, shift to concern.]"` },
      { name: "Guide the conversation forward", text: `"[Based on what you've learned, move toward a resolution. If it's noise: 'You're absolutely right, I'll turn it down.' If they need help: 'What can I do?' Avoid being dismissive or defensive. Close with an open door.]"` },
    ],
  },
  {
    type: "Personal",
    id: 1,
    question: "Tell me about yourself.",
    framework: "Narrative Framework",
    frameworkDesc: "Open with a thesis, anchor in one vivid experience, show range, synthesize forward.",
    steps: [
      { name: "Hook / thesis", text: `"The thread that connects everything in my path is a fascination with the moments where systems fail individual people - and what it looks like when someone steps into that gap..."` },
      { name: "Anchor story", text: `"I spent a summer volunteering at a community clinic where I saw patients fall through cracks between mental health and primary care. One woman in particular had been bouncing between providers for months with no continuity..."` },
      { name: "Show range", text: `"That same pattern showed up in a completely different context during my research on rural healthcare access, where I saw the same systemic gaps but at a population level..."` },
      { name: "Synthesis / forward look", text: `"What I've realized is that the people who make the biggest difference aren't necessarily the most specialized - they're the ones who see the whole person in context. That's the kind of practitioner I want to be..."` },
    ],
  },
  {
    type: "Behavioral",
    id: 17,
    question: "Describe a situation where you made a mistake that affected others. What did you do?",
    framework: "Behavioral Framework",
    frameworkDesc: "Answer directly, support with a specific anecdote, reflect, connect to your future.",
    steps: [
      { name: "Answer directly", text: `"The mistake that taught me the most was during a group research project where I took on too much responsibility and didn't delegate, which ultimately hurt the team's output..."` },
      { name: "Anecdote", text: `"I assumed I could handle the data analysis and the write-up myself. Two weeks before the deadline, I was behind on both. When I finally asked for help, my teammates had to scramble - and the analysis was weaker than it should have been because I hadn't brought in their perspectives earlier..."` },
      { name: "Reflection", text: `"What I learned is that taking on everything yourself isn't leadership - it's actually a failure to trust your team. I thought I was being helpful, but I was denying others the chance to contribute and creating a single point of failure..."` },
      { name: "Connection", text: `"In healthcare, this matters enormously. A physician who doesn't delegate, doesn't consult, and doesn't trust their team puts patients at risk. I've since become much more deliberate about asking for help early..."` },
    ],
  },
  {
    type: "Reflective",
    id: 7,
    question: "What are your strengths?",
    framework: "Behavioral Framework (Reflective)",
    frameworkDesc: "Same four steps, but the reflection carries the answer. The anecdote is brief proof.",
    steps: [
      { name: "Answer directly", text: `"My biggest strength is that I'm genuinely good at making people feel heard - even in difficult or high-pressure situations. It's not something I perform; it's how I naturally engage..."` },
      { name: "Anecdote (brief)", text: `"During my time volunteering at a crisis helpline, my supervisor pointed out that callers consistently stayed on the line longer with me and reported feeling more supported - not because I gave better advice, but because I asked better questions and let silence sit..."` },
      { name: "Reflection", text: `"I think this comes from a genuine curiosity about people rather than a technique I learned. The risk is that I can sometimes prioritize listening over acting - there are moments where someone needs direction, not another question..."` },
      { name: "Connection", text: `"In medicine, this strength translates directly to patient rapport and history-taking. But I'm also aware that I need to balance it with decisiveness, especially in acute situations..."` },
    ],
  },
]

export function ExampleAnswers() {
  const [activeType, setActiveType] = useState("Ethics")
  const example = examples.find((e) => e.type === activeType)

  return (
    <div className="mt-6 space-y-4">
      {/* Type pills */}
      <div className="flex flex-wrap gap-1.5">
        {examples.map(({ type }) => (
          <button
            key={type}
            onClick={() => setActiveType(type)}
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
              activeType === type
                ? `${typeColors[type]} ring-1 ring-current/20`
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            }`}
          >
            {type}
          </button>
        ))}
      </div>

      {example && (
        <>
          {/* Question */}
          <Card className="py-0 overflow-hidden">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <span className={`mt-0.5 inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-[11px] font-medium ${typeColors[example.type]}`}>
                  {example.type}
                </span>
                <p className="text-sm leading-relaxed text-foreground font-medium">{example.question}</p>
              </div>
            </CardContent>
          </Card>

          {/* Framework breakdown */}
          <Card className="py-0 overflow-hidden border-primary/10">
            <div className="border-b bg-muted/40 px-5 py-2.5">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{example.framework}</p>
              <p className="mt-0.5 text-xs text-muted-foreground/80">{example.frameworkDesc}</p>
            </div>
            <div className="divide-y">
              {example.steps.map((step, i) => (
                <div key={i} className="flex gap-3 px-5 py-3">
                  <span className="mt-px flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground">{i + 1}</span>
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-foreground">{step.name}</p>
                    <p className="text-xs leading-relaxed text-muted-foreground italic">{step.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Practice CTA */}
          <div className="flex items-center gap-3">
            <Link
              href={`/app?q=${example.id}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Practice this question <ArrowRight className="size-3" />
            </Link>
            <span className="text-xs text-muted-foreground">Answer out loud and get AI feedback on your approach</span>
          </div>
        </>
      )}
    </div>
  )
}
